import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { Switch } from "@/components/ui/switch";
import { enablePush, pushSupported } from "@/lib/push";
import { toast } from "sonner";

const RADII = [[500, "500 m"], [1000, "1 km"], [5000, "5 km"], [10000, "10 km"], [15000, "15 km"]];

export default function SettingsPage() {
  const nav = useNavigate();
  const { user, setUser, setAuthOpen } = useAuth();
  useEffect(() => { if (user === false) setAuthOpen(true); }, [user, setAuthOpen]);
  if (!user) return <div className="p-8 text-center text-slate-500">Kyçu për të parë cilësimet.</div>;

  const save = async (patch) => {
    try { const r = await api.put("/auth/me", patch); setUser(r.data); toast.success("U ruajt"); }
    catch { toast.error("Nuk u ruajt"); }
  };
  const toggle = async (v) => {
    if (v) {
      if (pushSupported() && Notification.permission === "default") enablePush(true).catch(() => {});
      navigator.geolocation?.getCurrentPosition(() => {}, () => toast.error("Lejo vendndodhjen që të funksionojë"));
    }
    save({ notify_nearby: v, ...(v && !user.nearby_radius ? { nearby_radius: 1000 } : {}) });
  };

  return (
    <div className="gs-fade px-4 pt-4 pb-8" data-testid="settings-page">
      <div className="flex items-center gap-3 mb-5">
        <button data-testid="settings-back" onClick={() => nav(-1)} className="w-10 h-10 rounded-full bg-white border border-slate-100 flex items-center justify-center"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-xl font-bold text-slate-900">Cilësimet</h1>
      </div>
      <NearbyCard user={user} toggle={toggle} save={save} />
      {user.role !== "admin" && <DeleteAccount />}
    </div>
  );
}

function DeleteAccount() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const { prompt } = useConfirm();
  const del = async () => {
    const pw = await prompt({ title: "Fshi llogarinë", message: "Llogaria dhe shpalljet e tua do të fshihen. Shkruaj fjalëkalimin për ta konfirmuar.", placeholder: "Fjalëkalimi", confirmText: "Fshi", danger: true, type: "password" });
    if (!pw) return;
    try { await api.delete("/auth/me", { data: { password: pw } }); toast.success("Llogaria u fshi"); await logout(); nav("/"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <button data-testid="settings-delete-account" onClick={del} className="mt-6 w-full h-12 rounded-2xl border border-rose-200 bg-rose-50 text-rose-600 text-sm font-bold flex items-center justify-center gap-2">
      <Trash2 className="w-4 h-4" />Fshi llogarinë time
    </button>
  );
}

function NearbyCard({ user, toggle, save }) {
  const on = !!user.notify_nearby;
  const radius = user.nearby_radius || 1000;
  const [r, setR] = useState(radius);
  useEffect(() => { setR(radius); }, [radius]);
  return (
    <div className="bg-white rounded-3xl p-4 border border-slate-100 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><MapPin className="w-5 h-5" /></span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900">Njoftimet për bizneset afër</p>
          <p className="text-xs text-slate-500">Merr njoftim si SMS kur i afrohesh një biznesi</p>
        </div>
        <Switch data-testid="settings-nearby-switch" checked={on} onCheckedChange={toggle} />
      </div>
      {on && (
        <div className="mt-4">
          <p className="text-xs font-semibold text-slate-500 mb-2">Sa larg të të njoftojë?</p>
          <div className="flex gap-2 overflow-x-auto snap-x pb-1" data-testid="settings-radius-wheel">
            {RADII.map(([v, l]) => (
              <button key={v} data-testid={`settings-radius-${v}`} onClick={() => { setR(v); save({ nearby_radius: v }); }}
                className={`snap-center shrink-0 px-4 h-10 rounded-full text-sm font-semibold border transition-colors ${r === v ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200"}`}>{l}</button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Funksionon kur aplikacioni është i hapur ose në sfond.</p>
        </div>
      )}
    </div>
  );
}
